/**
 * ShellContext — cross-component state for the app shell.
 *
 * Provides:
 *   - sidebar collapsed (desktop): persists to localStorage
 *   - mobile drawer open
 *   - which section submenu panel the user has dismissed
 *   - the command-palette open/setOpen pair (Stage 6 replaces the
 *     state hook; the same context surface stays)
 *
 * Placed alongside the shell so Stage 5 pages can read it if they
 * need to (e.g. to toggle the sidebar from a deep-nested CTA).
 */

import { useMediaQuery } from '@/hooks/use-media-query';
import { DEMO_FRAMED } from '@/lib/demo';
import { findNavSection } from '@/lib/topology';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const COLLAPSE_STORAGE_KEY = 'wv.dashboard.sidebar.collapsed';
const AI_ACTIVE_CONV_STORAGE_KEY = 'wv.dashboard.ai.activeConvId';

/** AIPane drill-down view — chat (active conv), switcher (history list), welcome (empty). */
export type AIPaneView = 'chat' | 'switcher' | 'welcome';

type ShellContextValue = {
  /** Desktop collapsed state (≥1024px: honours user pref; 768–1023px: auto-collapsed default, can un-collapse). */
  collapsed: boolean;
  setCollapsed: (next: boolean) => void;
  /** Mobile-only drawer open state (<768px). */
  mobileOpen: boolean;
  setMobileOpen: (next: boolean) => void;
  /** Current viewport class. */
  isMobile: boolean;
  /**
   * Section href whose submenu panel the user explicitly closed, or
   * `null`. The panel itself is derived from the URL (see
   * `findNavSection`) — this is the one bit of state that isn't, so a
   * user who dismisses the panel doesn't get it forced back open on
   * every navigation within the same section. Set it back to `null` to
   * re-open (navigating to another section does this implicitly,
   * because the stored href no longer matches).
   */
  submenuClosedFor: string | null;
  setSubmenuClosedFor: (href: string | null) => void;
  /** Right-hand Assistant pane open state. */
  aiOpen: boolean;
  setAiOpen: (next: boolean) => void;
  /**
   * True after the assistant pane has been opened at least once this
   * session. Drives the lazy-load gate in the Shell — the AIPane (and
   * its agents-sdk / @cloudflare/ai-chat / ai-sdk deps) is not even
   * mounted, let alone fetched, until the user first opens it. Once
   * flipped true it stays true so the pane stays mounted across
   * close/reopen and preserves the active conversation's WebSocket
   * + UI state.
   */
  aiHasEverOpened: boolean;
  /**
   * AIPane drill-down view selector — `chat` is the default (active
   * conversation), `switcher` is the history list, `welcome` is the
   * empty state (first-time / after `/forget`).
   */
  aiPaneView: AIPaneView;
  setAiPaneView: (next: AIPaneView) => void;
  /**
   * UUID of the conversation the pane is currently focused on, or
   * `null` for the welcome view. Persists to localStorage so reopening
   * the pane lands on the same conversation.
   */
  aiActiveConvId: string | null;
  setAiActiveConvId: (next: string | null) => void;
  /**
   * Org-switcher popover open state. Owned by the sidebar org pill
   * but surfaced here so the user pill menu's "Switch organisation"
   * row can open the same popover.
   */
  orgSwitcherOpen: boolean;
  setOrgSwitcherOpen: (next: boolean) => void;
};

const ShellContext = createContext<ShellContextValue | null>(null);

/**
 * Leading node the Shell slots into the Island header (the mobile
 * hamburger). A context — not a cloned prop — because the Island sits
 * below the router's `<Outlet />`, which accepts no props, so
 * `cloneElement` on Shell's direct child can never reach it.
 */
export const IslandLeadingContext = createContext<ReactNode>(null);

function readStoredBool(key: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback;
    return raw === '1' || raw === 'true';
  } catch {
    return fallback;
  }
}

function writeStoredBool(key: string, value: boolean) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {
    // ignore (private mode, quota, etc.)
  }
}

export function ShellProvider({ children }: { children: ReactNode }) {
  // mobile (<768) — no sidebar; driven entirely by drawer open state.
  const isMobile = useMediaQuery('max-md');
  // 768-1023 — "tablet"; auto-collapse default per shell-spec §5.
  const isTablet = useMediaQuery('md:max-lg');

  // Default to collapsed at every breakpoint. Users who previously
  // expanded the sidebar keep their preference via the stored flag;
  // first-time visitors see the icon-only rail.
  const [collapsed, setCollapsedState] = useState<boolean>(() =>
    readStoredBool(COLLAPSE_STORAGE_KEY, true),
  );

  // Belt-and-braces: if the stored flag is missing (e.g. private
  // browsing cleared localStorage between sessions) force collapsed
  // on the tablet band too — the icon rail is still the correct
  // default there and avoids re-opening on resize.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hadStored = window.localStorage.getItem(COLLAPSE_STORAGE_KEY) != null;
    if (!hadStored && isTablet) {
      setCollapsedState(true);
    }
  }, [isTablet]);

  const setCollapsed = useCallback((next: boolean) => {
    setCollapsedState(next);
    writeStoredBool(COLLAPSE_STORAGE_KEY, next);
  }, []);

  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the mobile drawer whenever we cross out of the mobile
  // breakpoint so state stays coherent on resize.
  useEffect(() => {
    if (!isMobile && mobileOpen) setMobileOpen(false);
  }, [isMobile, mobileOpen]);

  // Deliberately in-memory only: the panel is the sole route to child
  // pages, so a dismissal that outlived the tab would leave a returning
  // user staring at a rail with no way back into the section.
  // Seeded with the boot section inside www's hero frame (DEMO_FRAMED), so
  // the framed demo arrives on the rail alone instead of opening a panel the
  // hero's poster doesn't have.
  const [submenuClosedFor, setSubmenuClosedFor] = useState<string | null>(() =>
    DEMO_FRAMED ? (findNavSection(window.location.pathname)?.href ?? null) : null,
  );

  const [aiOpen, setAiOpenState] = useState(false);
  const [aiHasEverOpened, setAiHasEverOpened] = useState(false);
  // Wrap setAiOpen so the first `setAiOpen(true)` also flips the
  // `hasEverOpened` latch — that's what gates the lazy mount in Shell.
  const setAiOpen = useCallback((next: boolean) => {
    setAiOpenState(next);
    if (next) setAiHasEverOpened(true);
  }, []);
  // Close the AI pane when crossing to mobile (pane overlays the
  // island there; easier to just dismiss than stack panels).
  useEffect(() => {
    if (isMobile && aiOpen) setAiOpen(false);
  }, [isMobile, aiOpen, setAiOpen]);

  // Active chat conversation id — persists across pane open/close so
  // reopening the pane lands on the same conversation. `null` = welcome.
  const [aiActiveConvId, setAiActiveConvIdState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      return window.localStorage.getItem(AI_ACTIVE_CONV_STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const setAiActiveConvId = useCallback((next: string | null) => {
    setAiActiveConvIdState(next);
    if (typeof window === 'undefined') return;
    try {
      if (next) window.localStorage.setItem(AI_ACTIVE_CONV_STORAGE_KEY, next);
      else window.localStorage.removeItem(AI_ACTIVE_CONV_STORAGE_KEY);
    } catch {
      // ignore (private mode, quota)
    }
  }, []);

  // Pane drill-down view — defaults to 'chat' when there's an active
  // conv, 'welcome' otherwise. The pane component is responsible for
  // flipping to 'switcher' when the user taps the title pill.
  const [aiPaneView, setAiPaneView] = useState<AIPaneView>(aiActiveConvId ? 'chat' : 'welcome');

  const [orgSwitcherOpen, setOrgSwitcherOpen] = useState(false);

  const value = useMemo<ShellContextValue>(
    () => ({
      collapsed,
      setCollapsed,
      mobileOpen,
      setMobileOpen,
      isMobile,
      submenuClosedFor,
      setSubmenuClosedFor,
      aiOpen,
      setAiOpen,
      aiHasEverOpened,
      aiPaneView,
      setAiPaneView,
      aiActiveConvId,
      setAiActiveConvId,
      orgSwitcherOpen,
      setOrgSwitcherOpen,
    }),
    [
      collapsed,
      setCollapsed,
      mobileOpen,
      isMobile,
      submenuClosedFor,
      aiOpen,
      setAiOpen,
      aiHasEverOpened,
      aiPaneView,
      aiActiveConvId,
      setAiActiveConvId,
      orgSwitcherOpen,
    ],
  );

  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

export function useShell(): ShellContextValue {
  const ctx = useContext(ShellContext);
  if (!ctx) throw new Error('useShell must be used inside <ShellProvider>');
  return ctx;
}
